"use client";

import type { ReactNode } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { BellRing, Lock, ShieldCheck } from "lucide-react";
import { SiteHeader } from "./site-chrome";
import { usePrefs } from "./prefs-provider";
import { BrandLogo } from "./brand-logo";

type Props = {
  title: ReactNode;
  lead?: ReactNode;
  /** Changing the key animates between steps (form → verify). */
  stepKey?: string;
  children: ReactNode;
  footer?: ReactNode;
};

/**
 * Shared frame for login / register / password reset.
 * Desktop: form on one side, a calm brand panel on the other. Mobile: just the form.
 */
export function AuthShell({ title, lead, stepKey = "default", children, footer }: Props) {
  const { t } = usePrefs();
  const a = t.auth;
  const points = [
    { Icon: ShieldCheck, text: a.side1 },
    { Icon: BellRing, text: a.side2 },
    { Icon: Lock, text: a.side3 },
  ];

  return (
    <div className="min-h-screen bg-bg">
      <SiteHeader solid minimal />
      <main id="main-content" className="mx-auto grid min-h-screen max-w-6xl items-center gap-10 px-4 pb-16 pt-24 sm:px-6 lg:grid-cols-[minmax(0,26rem)_1fr] lg:gap-16 lg:pt-28">
        {/* Form column (reading-start side) */}
        <section className="mx-auto w-full max-w-md lg:mx-0">
          <div className="native-auth-brand">
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img src="/logo.png" alt="" className="native-auth-mark" />
            <p className="font-display text-[1.7rem] font-bold tracking-tight text-ink">
              Chati<span className="text-brand">Safe</span>
            </p>
          </div>
          <AnimatePresence mode="wait" initial={false}>
            <motion.div
              key={stepKey}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -6 }}
              transition={{ duration: 0.2, ease: [0.22, 1, 0.36, 1] }}
            >
              <h1 className="font-display text-3xl font-bold tracking-tight text-ink sm:text-[2rem]">{title}</h1>
              {lead ? <p className="mt-2 text-[15px] leading-relaxed text-ink-2">{lead}</p> : null}
            </motion.div>
          </AnimatePresence>
          <div className="mt-8">{children}</div>
          {footer ? <div className="native-auth-footer mt-8 border-t border-line pt-6 text-sm text-ink-2">{footer}</div> : null}
        </section>

        {/* Brand panel (desktop only) */}
        <aside className="hidden lg:block" aria-hidden>
          <div className="relative overflow-hidden rounded-3xl bg-brand-soft p-10">
            <div className="pointer-events-none absolute -end-16 -top-16 h-56 w-56 rounded-full bg-brand/10" />
            <BrandLogo href="" imageClassName="h-12 w-12" wordmarkAlways wordmarkClassName="text-2xl" />
            <h2 className="mt-8 font-display text-3xl font-bold leading-tight text-brand-ink">{a.sideTitle}</h2>
            <ul className="mt-8 space-y-5">
              {points.map(({ Icon, text }) => (
                <li key={text} className="flex items-start gap-3">
                  <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-surface text-brand shadow-1">
                    <Icon className="h-4.5 w-4.5" aria-hidden />
                  </span>
                  <p className="text-[15px] leading-relaxed text-ink">{text}</p>
                </li>
              ))}
            </ul>
          </div>
        </aside>
      </main>
    </div>
  );
}
